import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { createReceita, fetchContasReceita } from '../../../services/receitaService';

const ReceitaCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [contasOptions, setContasOptions] = useState([]);
  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    dataBalanco: '',
    pedido: '',
    conta: '',
    quantidade: '1'
  });
  const [errors, setErrors] = useState({});

  // Carregar opções para dropdowns
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const contas = await fetchContasReceita();
        setContasOptions(contas);
      } catch (err) {
        console.error('Erro ao carregar opções:', err);
      }
    };
    
    loadOptions();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.valor) {
      newErrors.valor = 'Valor é obrigatório';
    } else if (parseFloat(formData.valor) <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    if (!formData.dataBalanco) {
      newErrors.dataBalanco = 'Data do balanço é obrigatória';
    }

    if (!String(formData.conta).trim()) {
      newErrors.conta = 'Conta é obrigatória';
    }

    if (!formData.quantidade) {
      newErrors.quantidade = 'Quantidade é obrigatória';
    } else if (parseInt(formData.quantidade) <= 0) {
      newErrors.quantidade = 'Quantidade deve ser maior que zero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const receitaData = {
        ...formData,
        valor: parseFloat(formData.valor),
        quantidade: parseInt(formData.quantidade),
        pedido: formData.pedido ? parseInt(formData.pedido) : null,
        conta: formData.conta ? parseInt(formData.conta) : null
      };

      await createReceita(receitaData);
      navigate('/financeiro/receitas', { 
        state: { 
          message: 'Receita criada com sucesso!', 
          type: 'success' 
        } 
      });
    } catch (err) {
      setErrors({ 
        submit: err.message || 'Erro ao criar receita' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/receitas');
  };

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <div className="flex items-center mb-4">
        <button
          onClick={handleCancel}
          className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} size="lg" />
        </button>
        <h2 className="text-xl font-bold">Cadastrar Nova Receita</h2>
      </div>
      {/* `noValidate`: a validação em português deste formulário é a que o
          usuário deve ver — o balão nativo do navegador some sozinho, sai no
          idioma do navegador e mostra um campo por vez. */}


      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {errors.submit && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {errors.submit}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="descricao" className="block text-sm font-medium mb-1">
              Descrição *
            </label>
            <input
              type="text"
              id="descricao"
              value={formData.descricao}
              onChange={(e) => handleInputChange('descricao', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.descricao ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ex: Venda de Cursos Online"
            />
            {errors.descricao && (
              <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>
            )}
          </div>

          <div>
            <label htmlFor="valor" className="block text-sm font-medium mb-1">
              Valor *
            </label>
            <input
              type="number"
              id="valor"
              step="0.01"
              min="0"
              value={formData.valor}
              onChange={(e) => handleInputChange('valor', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.valor ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0,00"
            />
            {errors.valor && (
              <p className="mt-1 text-sm text-red-600">{errors.valor}</p>
            )}
          </div>

          <div>
            <label htmlFor="quantidade" className="block text-sm font-medium mb-1">
              Quantidade *
            </label>
            <input
              type="number"
              id="quantidade"
              min="1"
              value={formData.quantidade}
              onChange={(e) => handleInputChange('quantidade', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.quantidade ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="1"
            />
            {errors.quantidade && (
              <p className="mt-1 text-sm text-red-600">{errors.quantidade}</p>
            )}
          </div>

          <div>
            <label htmlFor="dataBalanco" className="block text-sm font-medium mb-1">
              Data do Balanço *
            </label>
            <input
              type="date"
              id="dataBalanco"
              value={formData.dataBalanco}
              onChange={(e) => handleInputChange('dataBalanco', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.dataBalanco ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.dataBalanco && (
              <p className="mt-1 text-sm text-red-600">{errors.dataBalanco}</p>
            )}
          </div>

          <div>
            <label htmlFor="pedido" className="block text-sm font-medium mb-1">
              Número do Pedido
            </label>
            <input
              type="number"
              id="pedido"
              value={formData.pedido}
              onChange={(e) => handleInputChange('pedido', e.target.value)}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Ex: 1001"
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="conta" className="block text-sm font-medium mb-1">
              Conta *
            </label>
            <div className="relative">
              <select
                id="conta"
                value={formData.conta}
                onChange={(e) => handleInputChange('conta', e.target.value)}
                className={`w-full border rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  errors.conta ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Selecione uma conta</option>
                {contasOptions.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.descricao || conta.nome || `Conta ${conta.id}`}
                  </option>
                ))}
              </select>
            </div>
            {errors.conta && (
              <p className="mt-1 text-sm text-red-600">{errors.conta}</p>
            )}
          </div>
        </div>

        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button
            type="button"
            onClick={handleCancel}
            className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
            disabled={loading}
           aria-label="Cancelar">
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button
            type="submit"
            disabled={loading}
            className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-blue-700 flex items-center gap-2 w-10 sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
           aria-label="Salvar">
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSave} />
            )}
            <span className="hidden sm:inline">
              {loading ? 'Salvando...' : 'Salvar'}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReceitaCreate;
